<template>
  <div>
    <slot></slot>
    <slot name="drawable"></slot>
  </div>
</template>

<script>
import {
  error,
  getModelPosition,
  getModelPositionByBoundingBox,
  loadBimfaceSDK,
  log,
  zoomToModel,
} from "../utils/bimface";
import { createId } from "../utils/string";
import bus from "../utils/bus";

const positionValidator = (value) => {
  return "x" in value && "y" in value && "z" in value;
};

export default {
  name: "Model",
  props: {
    id: [String, Number],
    fileId: [String, Number],
    viewToken: String,
    x: Number,
    y: Number,
    z: Number,
    position: {
      type: Object,
      default: () => ({ x: 0, y: 0, z: 0 }),
      validator: positionValidator,
    },
    scale: {
      type: Number,
      default: 1,
    },
    rotateX: { type: Number, default: 0 },
    rotateY: { type: Number, default: 0 },
    rotateZ: { type: Number, default: 0 },
    translucentOthers: Array | Number | String,
    hideOthers: Array | Number | String,
    rePosition: {
      type: Object,
      validator: positionValidator,
    },
    zoomTo: Boolean,
    show: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      modelId: null,
      _viewToken: null,
      modelData: null,
      drawableMng: null,
      toProvide: {
        viewer3D: null,
        model3D: null,
        modelUpdateVersion: 0,
      },
      basePos: null,
    };
  },
  inject: ["bimface"],
  provide() {
    return {
      model: this.toProvide,
    };
  },
  computed: {
    viewer3D() {
      return this.bimface.viewer3D;
    },
    isModelLoaded() {
      return this.bimface.model3D[this.modelId] !== undefined;
    },
    model3D() {
      return this.bimface.model3D[this.modelId];
    },
  },
  watch: {
    async fileId(newVal, oldVal) {
      this.viewer3D.removeModel(this.modelId);
      this.toProvide.model3D = null;
      await this.load();
    },
    async viewToken() {
      this.viewer3D.removeModel(this.modelId);
      await this.load();
    },
    "bimface.viewer3D": async function (newVal, oldVal) {
      // viewer3D加载完成，开始加载模型
      this.toProvide.viewer3D = newVal;
      log("viewer3D加载完成，开始加载模型", newVal);
      if (this.isModelLoaded) {
        log("模型已加载，不重复加载");
        return;
      }
      await this.load();
    },
    position(newVal) {
      this.updatePosition(newVal);
    },
    scale(newVal) {
      let base_pos = getModelPosition(this.model3D);
      this.model3D.setModelScale(base_pos, newVal);
    },
    rotateX(newVal) {
      let base_pos = getModelPosition(this.model3D);
      this.model3D.setModelRotationX(base_pos, newVal);
    },
    rotateY(newVal) {
      let base_pos = getModelPosition(this.model3D);
      this.model3D.setModelRotationY(base_pos, newVal);
    },
    rotateZ(newVal) {
      let base_pos = getModelPosition(this.model3D);
      this.model3D.setModelRotationZ(base_pos, newVal);
    },
    translucentOthers(newVal) {
      if (!this.hideOthers) {
        if (!newVal) {
          this.model3D.clearIsolation();
        } else {
          this.translucentOtherComponents(this.model3D, newVal);
        }
      }
    },
    hideOthers(newVal) {
      if (!newVal) {
        this.model3D.clearIsolation();
      } else {
        this.hideOtherComponents(this.model3D, newVal);
      }
    },
    rePosition: {
      handler(newVal) {
        let modelTransformation = this.model3D.getModelTransformation();
        // 当前坐标偏移值
        let currentTranslation = {
          x: modelTransformation[12],
          y: modelTransformation[13],
          z: modelTransformation[14],
        };
        // 目标坐标
        let targetPosition = {
          x: newVal.x - this.basePos.x,
          y: newVal.y - this.basePos.y,
          z: newVal.z - this.basePos.z,
        };
        // 距离目标坐标还需要的偏移值
        let diffTranslation = {
          x: targetPosition.x - currentTranslation.x,
          y: targetPosition.y - currentTranslation.y,
          z: targetPosition.z - currentTranslation.z,
        };
        if (this.basePos && this.model3D) {
          this.model3D.setModelTranslation(diffTranslation);
          if (this.zoomTo) {
            zoomToModel(this.model3D, this.viewer3D);
          }
        }
      },
      deep: true,
    },
    show(newVal) {
      newVal ? this.showModel(this.model3D) : this.hideModel(this.model3D);
    },
  },
  async mounted() {
    if (this.viewer3D) {
      await this.load();
    }
  },
  methods: {
    isBimfaceModel() {
      return true;
    },
    createModelId() {
      return createId();
    },
    async load() {
      if (this.fileId === undefined && this.viewToken === undefined) {
        error("file-id或view-token必须有一个不为空");
      }
      if (this.fileId) {
        // 检查viewToken是否存在
        if (this.bimface.modelData[this.fileId] === undefined) {
          let viewToken = await this.getViewTokenApi(this.fileId);
          this._viewToken = viewToken;
          this.modelData = await loadBimfaceSDK(viewToken);
        } else {
          this._viewToken = this.bimface.modelData[this.fileId].viewToken;
          this.modelData = this.bimface.modelData[this.fileId].modelData;
        }
      } else {
        this._viewToken = this.viewToken;
      }
      // log('加载模型', this._viewToken)
      this.modelId = this.id || this.createModelId();
      this.viewer3D.loadModel({
        viewToken: this._viewToken,
        modelId: this.modelId,
      });
    },
    async onModelLoaded(model3D) {
      this.toProvide.model3D = model3D;
      // 获取初始模型坐标
      let origin_pos = getModelPosition(model3D);
      model3D.setModelTranslation({
        x: (this.x || this.position.x || 0) - origin_pos.x,
        y: (this.y || this.position.y || 0) - origin_pos.y,
        z: (this.z || this.position.z || 0) - origin_pos.z,
      });
      let base_pos = await getModelPositionByBoundingBox(model3D);
      // 模型原始位置
      this.basePos = base_pos;
      this.scale && model3D.setModelScale(base_pos, this.scale);
      this.rotateX && model3D.setModelRotationX(base_pos, this.rotateX);
      this.rotateY && model3D.setModelRotationY(base_pos, this.rotateY);
      this.rotateZ && model3D.setModelRotationZ(base_pos, this.rotateZ);

      if (this.rePosition) {
        model3D.setModelTranslation({
          x: this.rePosition.x - base_pos.x,
          y: this.rePosition.y - base_pos.y,
          z: this.rePosition.z - base_pos.z,
        });
      }

      // 将摄像头聚焦到模型
      if (this.zoomTo) {
        zoomToModel(model3D, this.viewer3D);
      }

      this.show ? this.showModel(model3D) : this.hideModel(model3D);

      this.$emit("loaded", model3D, this.viewer3D);
      bus.$emit("modelLoaded", model3D, this.viewer3D);
    },
    updatePosition(position) {
      let origin_pos = getModelPosition(this.model3D);
      this.model3D.setModelTranslation({
        x: (position.x || 0) - origin_pos.x,
        y: (position.y || 0) - origin_pos.y,
        z: (position.z || 0) - origin_pos.z,
      });
    },
    translucentOtherComponents(model3D, ids) {
      model3D.isolateComponentsById(
        this.getIds(ids),
        Glodon.Bimface.Viewer.IsolateOption.MakeOthersTranslucent
      );
    },
    hideOtherComponents(model3D, ids) {
      model3D &&
        model3D.isolateComponentsById(
          this.getIds(ids),
          Glodon.Bimface.Viewer.IsolateOption.HideOthers
        );
    },
    getIds(ids) {
      return Array.isArray(ids) ? ids : [ids.toString()];
    },
    showModel(model3D) {
      model3D && model3D.showAllComponents();

      if (this.hideOthers) {
        this.hideOtherComponents(model3D, this.hideOthers);
      } else if (this.translucentOthers) {
        this.translucentOtherComponents(model3D, this.translucentOthers);
      }

      // 触发构件更新自己的显示状态
      this.toProvide.modelUpdateVersion++;
    },
    hideModel(model3D) {
      model3D && model3D.hideAllComponents();
    },
  },
  beforeDestroy() {
    this.viewer3D && this.viewer3D.removeModel(this.modelId);
  },
};
</script>

<style lang="scss" scoped></style>
